import {
  SIGNER_FIELD_TEXT_BASELINE_OFFSET,
  type LayoutField,
} from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";
import {
  extractSignerTemplateMarkersFromPdf,
  getSignerTemplateMarkerMask,
  restoreSignerTemplateMarkerUnderline,
} from "@/components/features/matter/file-repo/signature-requests/signatureRequestTemplateFields";
import { SIGNER_FONT_ASSETS } from "./signerFieldTextStyle";

const fontBytes = new Map<string, Promise<ArrayBuffer>>();

function readSignerFont(family: keyof typeof SIGNER_FONT_ASSETS) {
  const existing = fontBytes.get(family);
  if (existing) return existing;
  const request = fetch(
    `/lib/webviewer/ui/assets/fonts/webfonts/${SIGNER_FONT_ASSETS[family]}.ttf`,
  )
    .then((response) => {
      if (!response.ok)
        throw new Error("Unable to load signing font. Please try again.");
      return response.arrayBuffer();
    })
    .catch((error: unknown) => {
      fontBytes.delete(family);
      throw error;
    });
  fontBytes.set(family, request);
  return request;
}

export type SignedSignatureRequestField = LayoutField & {
  checked?: boolean;
  imageDataUrl?: string;
};

type SignedFieldValues = Record<string, string>;

function isSignatureField(field: LayoutField) {
  return field.type === "signature" || field.type === "initials";
}

function isCheckboxField(field: LayoutField) {
  return field.type === "checkbox";
}

export async function generateSignedSignatureRequestPdfBlob({
  fields,
  fieldValues,
  sourceUrl,
}: {
  fields: SignedSignatureRequestField[];
  fieldValues: SignedFieldValues;
  sourceUrl: string;
}) {
  const { PDFDocument, rgb } = await import("pdf-lib");
  const { default: fontkit } = await import("@pdf-lib/fontkit");
  const existingPdfBytes = await fetch(sourceUrl).then((response) =>
    response.arrayBuffer(),
  );
  const signerTemplateMarkers = await extractSignerTemplateMarkersFromPdf(
    existingPdfBytes,
  ).catch((error: unknown) => {
    console.error("Failed to clean signer template fields", error);
    return [];
  });
  const pdfDocument = await PDFDocument.load(existingPdfBytes);
  pdfDocument.registerFontkit(fontkit);
  const families = new Set(
    fields
      .filter((field) => field.type === "text" || field.type === "date")
      .map((field) => field.fontFamily ?? "Times New Roman"),
  );
  const fonts = new Map(
    await Promise.all(
      [...families].map(
        async (family) =>
          [
            family,
            await pdfDocument.embedFont(await readSignerFont(family), {
              subset: true,
            }),
          ] as const,
      ),
    ),
  );

  signerTemplateMarkers.forEach((marker) => {
    const page = pdfDocument.getPage(marker.page - 1);
    const { width: pageWidth, height: pageHeight } = page.getSize();
    page.drawRectangle({
      color: rgb(1, 1, 1),
      ...getSignerTemplateMarkerMask(marker, pageWidth, pageHeight),
    });
    restoreSignerTemplateMarkerUnderline(page, marker, rgb);
  });

  for (const field of fields) {
    const page = pdfDocument.getPage(field.page - 1);
    const { width: pageWidth, height: pageHeight } = page.getSize();

    if (isSignatureField(field)) {
      if (!field.imageDataUrl) continue;

      const signatureImage = await pdfDocument.embedPng(field.imageDataUrl);
      page.drawImage(signatureImage, {
        x: field.x * pageWidth,
        y: pageHeight - (field.y + field.height) * pageHeight,
        width: field.width * pageWidth,
        height: field.height * pageHeight,
      });
      continue;
    }

    if (isCheckboxField(field)) {
      if (!field.checked) continue;

      const boxX = field.x * pageWidth;
      const boxY = pageHeight - (field.y + field.height) * pageHeight;
      const boxW = field.width * pageWidth;
      const boxH = field.height * pageHeight;
      const insetX = boxW * 0.25;
      const insetY = boxH * 0.2;

      page.drawLine({
        start: { x: boxX + insetX, y: boxY + boxH * 0.45 },
        end: { x: boxX + boxW * 0.45, y: boxY + insetY },
        thickness: 1.5,
      });
      page.drawLine({
        start: { x: boxX + boxW * 0.45, y: boxY + insetY },
        end: { x: boxX + boxW - insetX, y: boxY + boxH - insetY },
        thickness: 1.5,
      });
      continue;
    }

    const value = (fieldValues[field.id] ?? "").trim();
    if (!value) continue;

    const font = fonts.get(field.fontFamily ?? "Times New Roman");
    if (!font) throw new Error("The signer text font could not be loaded.");
    const requestedSize = field.fontSize ?? 11;
    const availableWidth = Math.max(field.width * pageWidth - 8, 1);
    const size =
      requestedSize *
      Math.min(
        1,
        availableWidth /
          Math.max(font.widthOfTextAtSize(value, requestedSize), 1),
      );

    page.drawText(value, {
      x: field.x * pageWidth + 4,
      y:
        pageHeight -
        (field.y + field.height) * pageHeight +
        SIGNER_FIELD_TEXT_BASELINE_OFFSET,
      size,
      font,
    });
  }

  const bytes = await pdfDocument.save();
  return new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
}
